var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from '../i18n';
import { LocaleController } from '../i18n/LocaleController';
import { fmtBytes, fmtDate } from '../format';
import { CHART_THEMES } from '../chart-themes';
import './vitesse-icon';
import './inputs/vitesse-number-input';
import './inputs/vitesse-toggle';
/** Past this fraction of the allowance the storage copy warns — early enough to act on, late
 *  enough not to cry wolf at half a plan. */
const WARN_AT = 0.8;
/** The donut's wedge hues — the app's own chart ramp, in the order the AI usage page uses it, so
 *  a breakdown here reads as the same product as a breakdown there. */
const WEDGES = ['frost', 'mint', 'peach', 'candy', 'citrus'];
const WEDGE_COLORS = WEDGES.map((n) => CHART_THEMES[n].line);
/** fmtBytes with a trailing `.0` trimmed — "2 TB", not "2.0 TB" — for the round allowances that
 *  read cleaner without a decimal. Keeps "1.4 TB" intact. */
const fmtBytesTrim = (n) => fmtBytes(n).replace(/\.0(?= )/, '');
let VitesseBillingPanel = class VitesseBillingPanel extends LitElement {
    constructor() {
        super(...arguments);
        /** True while a tier change is being started, so the buttons say what is happening. */
        this.changingTier = false;
        /*
         * WHICH VIEW IS SHOWING — plan / payment / credits / storage.
         *
         * A property rather than internal state, because the tab strip lives in the PAGE above this
         * element — the same division as everywhere else, where the panel renders and the page wires.
         */
        this.view = 'plan';
        /** Set by the host while a purchase is being started, so the buttons can say what is happening
         *  instead of appearing dead. */
        this.buying = false;
        /** True while Stripe's embedded form is mounted in the checkout host. The pack buttons hide:
         *  starting a second checkout while one is open produces two payment intents for one intent. */
        this.checkoutOpen = false;
        /** A failure to REPORT, never to hide: a blank panel reads as "you are storing nothing". */
        this.error = '';
        /** Why the last auto top-up change was refused. Shown beside the controls rather than at the top
         *  of the panel: an error about a number is useless three sections away from the input. */
        this.autoTopupError = '';
        this.budgetError = '';
        /** The typed override, held separately from the stored value so a half-typed number is not saved. */
        this.budgetDraft = '';
        /** The custom top-up field. A STRING, not a number: an empty input is '' and coercing it to 0
         *  would render a zero the customer never typed. */
        this.customUsd = '';
        /** True while a card is being added, so the button says what is happening. */
        this.addingCard = false;
        /** What is TYPED in the auto top-up fields, before it is saved. Strings, because an empty input
         *  is '' and coercing that to 0 renders a zero the customer never entered. */
        this.autoThreshold = '';
        this.autoAmount = '';
        this.loc = new LocaleController(this);
    }
    /** LIGHT DOM — the shared stylesheet lives in the host document, so a shadow root would put
     *  every `.base-*` class (tabs, buttons, rows, hints) out of reach. */
    createRenderRoot() {
        return this;
    }
    /** The host owns the money: it holds the session, the token and Stripe.js. The panel only
     *  says which pack was clicked. A component inside someone else's dialog that could POST a payment
     *  is a component whose blast radius is not its own. */
    buy(packId) {
        this.dispatchEvent(new CustomEvent('vitesse-billing-buy', { detail: { packId }, bubbles: true, composed: true }));
    }
    cancelCheckout() {
        this.dispatchEvent(new CustomEvent('vitesse-billing-cancel', { bubbles: true, composed: true }));
    }
    render() {
        void this.loc;
        // An error REPLACES nothing — it is reported above whatever did load. A failed storage read
        // should not hide a plan and a balance that arrived fine.
        return html `
      <div class="billing-root">
        ${this.error ? html `<p class="billing-err">${this.error}</p>` : nothing}
        ${this.view === 'plan'
            ? html `${this.renderPlanStats()} ${this.checkoutOpen ? this.checkoutHost() : this.renderMatrix()}`
            : this.view === 'payment'
                ? this.renderPayment()
                : this.view === 'credits'
                    ? this.renderCredits()
                    : this.renderStorage()}
      </div>
    `;
    }
    /*
     * THE PLAN'S FIGURES — relays, users, dashboards, bandwidth — used out of allowed.
     *
     * No "Plan: Ultra" headline: the matrix right below marks the current tier with the signature
     * band and a badge, so a heading restating it was the same fact twice at two sizes. What the
     * headline cannot say — how much of what you bought is in use — is what this strip is for.
     */
    /*
     * WHAT THIS ORG IS USING, against what its plan allows — the usage-summary every billing console
     * settles on: one row per metric, the figure as "used / limit", and a thin meter under the ones
     * that have a ceiling to fill. A metric with no ceiling (Unlimited) shows the figure and no bar;
     * a bare count (relays) shows just the number. Quiet, scannable, honest about headroom.
     */
    renderPlanStats() {
        const p = this.plan;
        if (!p)
            return nothing;
        const relays = p.relays ?? 1;
        return html `
      <div class="billing-card">
        <div class="billing-usage">
          ${this.usageRow(relays === 1 ? t('billing.relayOne') : t('billing.relaysLabel'), relays, undefined, String)}
          ${this.usageRow(t('billing.users'), p.used?.['telemetry.max_users'], p.caps['telemetry.max_users'], String)}
          ${this.usageRow(t('billing.dashboards'), p.used?.['telemetry.max_dashboards'], p.caps['telemetry.max_dashboards'], String)}
          <!-- Bandwidth is metered PER RELAY, so the note says so on every plan (not only multi-relay
               ones) — the ceiling is per relay, not the org total a reader might assume. -->
          ${this.usageRow(t('billing.bandwidth'), p.used?.['telemetry.max_bandwidth_bytes'], p.caps['telemetry.max_bandwidth_bytes'], fmtBytesTrim, true)}
        </div>
      </div>
    `;
    }
    /** One usage row: label on the left, `used / limit` on the right, and a fill meter beneath when
     *  the limit is a finite number. `limit === null` is Unlimited (figure, no bar); `limit`
     *  undefined is a bare count (just the number, e.g. relays). Omitted entirely when there is
     *  nothing measured. */
    usageRow(label, used, limit, fmt, perRelay = false) {
        if (used === undefined && limit === undefined)
            return nothing;
        const finite = typeof limit === 'number';
        const over = finite && used !== undefined && used >= limit;
        const pct = finite && used !== undefined ? Math.min(100, Math.round((used / limit) * 100)) : null;
        return html `
      <div class="billing-usage-row">
        <div class="billing-usage-head">
          <span class="billing-usage-label"
            >${label}${perRelay ? html `<span class="billing-sub"> ${t('billing.perRelaySuffix')}</span>` : nothing}</span
          >
          <span class="billing-usage-val ${over ? 'over' : ''}">
            ${used !== undefined ? html `<span class="u">${fmt(used)}</span>` : nothing}${used !== undefined && limit !== undefined
            ? html `<span class="sep"> / </span>`
            : nothing}${limit === null ? t('billing.unlimited') : limit !== undefined ? fmt(limit) : nothing}
          </span>
        </div>
        ${pct !== null
            ? html `<div class="billing-usage-bar"><span class=${over ? 'over' : ''} style=${`width:${pct}%`}></span></div>`
            : nothing}
      </div>
    `;
    }
    /*
     * THE PLANS — what you are on, and what else there is.
     *
     * The layout is the one every pricing page converged on (Stripe, Vercel, Linear, the Tailwind
     * Plus pricing sections): a row of vertical tier CARDS, each self-contained — name, price, its
     * one action, then its own checkmark feature list — rather than a feature×tier grid. A card reads
     * top-to-bottom the way a buyer decides ("is THIS the plan for me"), where the grid asked them to
     * scan a spreadsheet. Every card lists the same features in the same order, so reading two cards
     * side by side is still a comparison, just a legible one.
     *
     * The catalog is the same one the plans console renders, served by the plans worker, so the two
     * surfaces cannot quote different prices — nothing here is computed from a local price list.
     */
    renderMatrix() {
        const cat = this.catalog;
        // Absent means "could not load", and it is said out loud. An empty grid would read as "there
        // are no plans", which is a worse lie than an honest failure.
        if (!cat)
            return nothing;
        if (!cat.tiers.length)
            return html `<p class="billing-sub">${t('billing.plansUnavailable')}</p>`;
        const current = this.plan?.tierId;
        const currentRank = cat.tiers.find((tr) => tr.id === current)?.rank ?? -1;
        // Every tier including the free floor, by ruling: the floor is a real thing an org can be on,
        // and a lineup that hides it cannot show what the first paid tier actually adds. The floor just
        // gets no action — there is nothing to buy, and leaving is the cancel button below.
        const shown = [...cat.tiers].sort((a, b) => a.rank - b.rank);
        // A cancel is offered only when there is a paid plan to leave. On the free floor there is
        // nothing to cancel, so the button would be a control that can only fail.
        const onPaidPlan = currentRank > 0 && cat.tiers.find((tr) => tr.id === current)?.purchasable !== false;
        const features = this.planFeatures(cat, shown);
        // No separate cancel button: dropping to the free floor IS cancelling, so the floor's card
        // carries a "Downgrade" while a paid plan is active. One place to leave, in the lineup.
        // The grid owns the row tracks (head + one per feature + action); each card is a subgrid that
        // spans them, so a feature row is as tall as its tallest cell ACROSS the four plans and every
        // card's copy of that row sits at the same y. That shared-track alignment — not per-card
        // heights — is what lets a reader scan one feature straight across the plans.
        return html `
      <div class="billing-plan-grid" style=${`--plan-cols:${shown.length};--plan-rows:${features.length + 2}`}>
        ${shown.map((tr) => this.renderPlanCard(tr, features, tr.id === current, currentRank, onPaidPlan))}
      </div>
    `;
    }
    /**
     * The ordered feature list every card renders, built ONCE so every card shows the same features
     * in the same rows — that alignment is what keeps a row of cards readable as a comparison. The
     * union of every tier's capability keys, minus internal gate plumbing, plus the collapsed
     * integration-connections figure as a final synthetic row.
     */
    planFeatures(cat, shown) {
        const byKey = new Map((cat.capabilities ?? []).map((c) => [c.key, c]));
        const perRelay = new Set(cat.perRelayLimits);
        const keys = [...new Set(shown.flatMap((tr) => Object.keys(tr.cells)))]
            // Gate plumbing is not a feature a buyer chooses on — every purchasable tier has it, so it is
            // the same tick on every card and only adds noise.
            .filter((k) => !byKey.get(k)?.internal)
            .filter((k) => !INTEGRATION_MAXES.includes(k))
            .sort();
        const feats = keys.map((k) => ({
            key: k,
            label: byKey.get(k)?.label ?? capLabel(k),
            help: byKey.get(k)?.help ?? '',
            perRelay: perRelay.has(k.split('.').pop() ?? k),
        }));
        // THE INTEGRATION LIMITS COLLAPSE TO ONE FEATURE. DB, broker and API counts are three numbers a
        // buyer reads as one question — how many external systems can I connect — so they are summed
        // into one figure, with the per-shape split kept in the row's hover title.
        if (shown.some((tr) => INTEGRATION_MAXES.some((k) => tr.cells[k] !== undefined))) {
            feats.push({ key: INTEGRATIONS_KEY, label: t('billing.integrationConnections'), help: t('billing.integrationConnectionsHelp'), perRelay: false });
        }
        return feats;
    }
    /** One tier as a card. EVERY card has the same slots in the same order — name, price+cadence,
     *  feature list, then the action LAST — so nothing above the features is conditional and the
     *  feature rows (and the divider above them) line up across every card. */
    renderPlanCard(tr, features, isCurrent, currentRank, onPaidPlan) {
        const isUpgrade = tr.rank > currentRank;
        const isFloor = !tr.purchasable; // the free floor — not a purchase; leaving a paid plan lands here
        // The action, pinned to the card's BOTTOM (see `.billing-pick { margin-top: auto }`):
        //   · current tier → none (the band names it).
        //   · free floor while on a paid plan → "Downgrade", which cancels the subscription.
        //   · any other purchasable tier → Upgrade (blue, moving up) or Downgrade (red, moving down).
        const action = isCurrent
            ? nothing
            : isFloor
                ? onPaidPlan
                    ? html `<button class="base-button base-button-md billing-pick base-button-red" ?disabled=${this.changingTier} @click=${this.cancelPlan}>
              ${this.changingTier ? t('billing.working') : t('billing.downgrade')}
            </button>`
                    : nothing
                : html `<button
            class="base-button base-button-md billing-pick ${isUpgrade ? 'base-button-blue' : 'base-button-red'}"
            ?disabled=${this.changingTier}
            @click=${() => this.pickTier(tr.id)}
          >
            ${this.changingTier ? t('billing.working') : isUpgrade ? t('billing.upgrade') : t('billing.downgrade')}
          </button>`;
        // Direct grid children, NOT a nested <ul>: each must land on its OWN subgrid row so the same
        // feature aligns across cards. Order: head, one cell per feature, action last.
        return html `
      <div class="billing-plan-card ${isCurrent ? 'current' : ''}">
        <div class="billing-plan-head">
          <div class="billing-plan-name">${tr.label}</div>
          <!-- The price is PER RELAY — the invoice multiplies by the relay count — and the cadence
               shows on EVERY card, the free floor included. -->
          <div class="billing-plan-price">
            ${fmtCents(tr.priceCents)}<span class="billing-plan-cadence">${t('billing.perMonthPerRelay')}</span>
          </div>
        </div>
        ${features.map((f) => this.renderPlanFeature(tr, f))}
        <div class="billing-plan-action">${action}</div>
      </div>
    `;
    }
    /** One feature cell: a mark, then the tier's VALUE-then-label as one text run ("200 GB Org
     *  storage", "7 d History retention") — no separate right-aligned column to collide with the
     *  label. Included is a GREEN check; a feature the tier does NOT carry is a grey dash + grey text,
     *  so the eye catches what's included and the rest recedes. */
    renderPlanFeature(tr, f) {
        if (f.key === INTEGRATIONS_KEY) {
            const cells = INTEGRATION_MAXES.map((k) => tr.cells[k]);
            const unlimited = cells.some((v) => v === null);
            const total = cells.reduce((n, v) => n + (typeof v === 'number' ? v : 0), 0);
            const off = !unlimited && total === 0;
            const tip = cells.map((v, j) => `${['DB', 'Broker', 'API'][j]} ${v === null ? '∞' : Number(v ?? 0)}`).join(' · ');
            const val = unlimited ? t('billing.unlimited') : String(total);
            return html `<div class="billing-plan-feat ${off ? 'off' : ''}" title=${tip}>
        <vitesse-icon name=${off ? 'minus' : 'check'} size="14"></vitesse-icon>
        <span class="feat-text">${off ? nothing : html `<span class="feat-val">${val}</span> `}${f.label}</span>
      </div>`;
        }
        const v = tr.cells[f.key];
        const off = isFeatureOff(f.key, v);
        const isBool = typeof v === 'boolean';
        // Booleans carry no figure (the check IS the value); everything else leads with its value.
        const val = off || isBool ? null : cellValue(f.key, v, tr.id);
        return html `<div class="billing-plan-feat ${off ? 'off' : ''}" title=${f.help}>
      <vitesse-icon name=${off ? 'minus' : 'check'} size="14"></vitesse-icon>
      <span class="feat-text"
        >${val != null ? html `<span class="feat-val">${val}</span> ` : nothing}${f.label}${f.perRelay
            ? html `<span class="billing-sub"> ${t('billing.perRelaySuffix')}</span>`
            : nothing}</span
      >
    </div>`;
    }
    /** Leave the paid plan and return to the floor. The panel only says "cancel"; the host runs the
     *  Stripe cancellation and re-reads the tier from the webhook-driven grant. */
    cancelPlan() {
        if (!globalThis.confirm?.(t('billing.cancelPlanConfirm')))
            return;
        this.dispatchEvent(new CustomEvent('vitesse-billing-cancel-plan', { bubbles: true, composed: true }));
    }
    /** The panel never talks to Stripe. It says which tier was clicked; the host owns the money. */
    pickTier(tierId) {
        this.dispatchEvent(new CustomEvent('vitesse-billing-tier', { detail: { tierId }, bubbles: true, composed: true }));
    }
    /** The div Stripe mounts into, rendered by whichever view opened it. The host div is empty in
     *  the template so Lit never touches the children the page appends into it. */
    checkoutHost() {
        return html `
      <div class="billing-checkout">
        <div class="billing-checkout-host"></div>
        <!-- Both controls together, bottom-RIGHT where a dialog puts its actions: cancel on the
             LEFT (red on hover — it abandons the form), and the host appends its Save button here
             to the RIGHT of it. One row, so the two are read as one decision. -->
        <div class="billing-checkout-actions">
          <button class="base-button base-button-sm base-button-red" @click=${this.cancelCheckout}>
            ${t('billing.cancelCheckout')}
          </button>
        </div>
      </div>
    `;
    }
    renderCredits() {
        const c = this.credits;
        if (!c)
            return this.error ? nothing : html `<p class="billing-sub">${t('billing.loading')}</p>`;
        return html `
      <div class="billing-card">
        <div class="billing-row">
          <div>
            <!-- The available balance (not the raw balance, which overstates what can be spent while
                 steps are in flight — the held figure is named separately, never silently added). No
                 "{{amount}} available" caption under it: the figure and the view name already say it. -->
            <div class="billing-big">${fmtUsd(c.availableUsd)}</div>
          </div>
          ${c.heldUsd > 0 ? html `<span class="billing-sub">${t('billing.creditsHeld', { amount: fmtUsd(c.heldUsd) })}</span>` : nothing}
        </div>
        ${c.suspended ? html `<p class="billing-note over">${t('billing.creditsSuspended')}</p>` : nothing}
        ${this.checkoutOpen
            ? this.checkoutHost()
            : html `
              <div class="billing-packs">
                ${c.packs.map((pk) => html `
                    <button class="base-button base-button-blue" ?disabled=${this.buying || c.suspended} @click=${() => this.buy(pk.id)}>
                      <vitesse-icon name="zap" size="14"></vitesse-icon>${this.buying ? t('billing.buying') : pk.label}
                    </button>
                  `)}
                <!-- CUSTOM. The packs are denominations a customer reads at a glance; this is for
                     the customer who knows the number they want. The panel does NOT validate the
                     range: the server owns the bounds, and a second copy here would drift from the
                     one actually enforced. -->
                <form class="billing-custom" @submit=${this.buyCustom}>
                  <span class="billing-sub">$</span>
                  <vitesse-number-input
                    size="sm"
                    integer
                    .min=${5}
                    .value=${this.customUsd}
                    @change=${(e) => (this.customUsd = e.detail.value)}
                  ></vitesse-number-input>
                  <button
                    class="base-button base-button-blue"
                    ?disabled=${this.buying || c.suspended || !this.customUsd}
                    @click=${this.buyCustom}
                  >
                    ${this.buying ? t('billing.buying') : t('billing.buyCustom')}
                  </button>
                </form>
              </div>
            `}
        <p class="billing-note">${t('billing.creditsPrepaid')}</p>
      </div>
      ${this.renderAutoTopup()} ${this.renderBudget()}
    `;
    }
    /** Buy a custom amount. The panel sends what was typed; the server decides whether it is
     *  allowed, and its refusal is what the customer reads. */
    buyCustom(e) {
        e.preventDefault();
        const usd = Number(this.customUsd);
        if (!Number.isFinite(usd))
            return;
        this.dispatchEvent(new CustomEvent('vitesse-billing-buy', { detail: { customUsd: usd }, bubbles: true, composed: true }));
    }
    /*
     * THE PAYMENT VIEW — saved cards, and the Stripe form when one is being added.
     *
     * The auto-top-up controls that do the charging stay on the credits view where the balance they
     * refill is; this view is only the instruments.
     */
    renderPayment() {
        const cards = this.credits?.cards;
        if (!cards)
            return this.error ? nothing : html `<p class="billing-sub">${t('billing.loading')}</p>`;
        return html `
      <div class="billing-card">
        <div class="billing-row">
          <h4 class="billing-h">${t('billing.paymentMethods')}</h4>
          <!-- The button DISAPPEARS while a card is being added — a second "add a card" mid-form
               would open a second Stripe element for one intent. It comes back when the form closes. -->
          ${this.addingCard || this.checkoutOpen
            ? nothing
            : html `<button class="base-button base-button-sm base-button-blue" @click=${this.addCard}>
                <vitesse-icon name="credit-card" size="14"></vitesse-icon>${t('billing.addCard')}
              </button>`}
        </div>
        ${cards.length === 0 && !this.checkoutOpen
            ? html `<p class="billing-sub">${t('billing.noCard')}</p>`
            : html `
              <div class="base-inventory-list billing-cardlist">
                ${cards.map((c) => html `
                    <div class="base-inventory-row">
                      <div class="base-inventory-titles">
                        <span class="base-inventory-name">
                          <span class="billing-brand">${c.brand}</span>
                          <span class="billing-dots">•••• ${c.last4}</span>
                          ${c.isDefault ? html `<span class="billing-badge">${t('billing.defaultCard')}</span>` : nothing}
                        </span>
                        <span class="base-field-hint">${String(c.expMonth).padStart(2, '0')}/${String(c.expYear).slice(-2)}</span>
                      </div>
                      <button class="base-button base-button-sm base-button-red" @click=${() => this.removeCard(c.id)}>
                        <vitesse-icon name="trash-2" size="14"></vitesse-icon>${t('billing.removeCard')}
                      </button>
                    </div>
                  `)}
              </div>
            `}
        ${this.checkoutOpen ? this.checkoutHost() : nothing}
      </div>
    `;
    }
    addCard() {
        this.dispatchEvent(new CustomEvent('vitesse-billing-add-card', { bubbles: true, composed: true }));
    }
    removeCard(paymentMethodId) {
        this.dispatchEvent(new CustomEvent('vitesse-billing-remove-card', { detail: { paymentMethodId }, bubbles: true, composed: true }));
    }
    renderAutoTopup() {
        const a = this.credits?.autoTopup;
        if (!a)
            return nothing;
        /*
         * THE FIELDS ARE ALWAYS VISIBLE, and that is a fix rather than a preference: they used to
         * render only once auto top-up was ENABLED, but enabling submits the stored amount — zero for
         * an org that never configured it — which the server rightly refuses. A control whose
         * precondition is only reachable through itself. Defaults are suggested rather than stored.
         */
        const threshold = this.autoThreshold || (a.thresholdUsd > 0 ? String(a.thresholdUsd) : '10');
        const amount = this.autoAmount || (a.amountUsd > 0 ? String(a.amountUsd) : '50');
        const submit = (over) => this.submitAutoTopup({
            ...a,
            thresholdUsd: Number(this.autoThreshold || threshold),
            amountUsd: Number(this.autoAmount || amount),
            ...over,
        });
        /*
         * DEBOUNCED, because the base number input reports every edit — right for a filter box, wrong
         * for a control that SAVES: every-edit semantics would send a request per keystroke (and per
         * spinner press). The toggle still submits immediately; only typing coalesces.
         */
        const submitSoon = () => {
            clearTimeout(this.autoSubmitTimer);
            this.autoSubmitTimer = setTimeout(() => {
                if (a.enabled)
                    submit({});
            }, 600);
        };
        return html `
      <div class="billing-card">
        <label class="billing-row billing-toggle-row">
          <span class="billing-h">${t('billing.autoTopup')}</span>
          <vitesse-toggle
            size="sm"
            .checked=${a.enabled}
            @change=${(e) => submit({ enabled: e.detail.checked })}
          ></vitesse-toggle>
        </label>
        <div class="billing-field-row">
          <span class="billing-sub">${t('billing.autoTopupWhen')}</span>
          <vitesse-number-input
            size="sm"
            integer
            .min=${1}
            .value=${threshold}
            @change=${(e) => {
            this.autoThreshold = e.detail.value;
            submitSoon();
        }}
          ></vitesse-number-input>
          <span class="billing-sub">${t('billing.autoTopupAdd')}</span>
          <vitesse-number-input
            size="sm"
            integer
            .min=${5}
            .step=${5}
            .value=${amount}
            @change=${(e) => {
            this.autoAmount = e.detail.value;
            submitSoon();
        }}
          ></vitesse-number-input>
        </div>
        ${a.failing ? html `<p class="billing-note warn">${t('billing.autoTopupFailing')}</p>` : nothing}
        ${this.autoTopupError ? html `<p class="billing-err">${this.autoTopupError}</p>` : nothing}
      </div>
    `;
    }
    /*
     * THE MONTHLY AUTO TOP-UP CEILING.
     *
     * Worded as a limit on AUTOMATIC charging, because that is what it is. It does not stop anyone
     * spending credits they hold or buying more themselves — a person deciding to spend money is what
     * this product is for, and a cap that overruled them would be the automation in charge.
     *
     * Shown whether or not the org set one: a default inherited from the plan is still a ceiling that
     * can stop a top-up, and hiding it would hide the reason. The spend sentence carries the numbers;
     * there is deliberately no progress bar — the sentence says it, and a bar under a dollar figure
     * reads as a meter running out.
     */
    renderBudget() {
        const b = this.credits?.budget;
        if (!b)
            return nothing;
        const limitUsd = b.limitMicros == null ? null : b.limitMicros / 1_000_000;
        const emit = (detail) => {
            this.budgetError = '';
            this.dispatchEvent(new CustomEvent('save-budget', { detail, bubbles: true, composed: true }));
        };
        return html `
      <div class="billing-card">
        <label class="billing-row billing-toggle-row">
          <span class="billing-h">${t('billing.budgetTitle')}</span>
          <vitesse-toggle
            size="sm"
            .checked=${b.enabled}
            @change=${(e) => emit({ enabled: e.detail.checked })}
          ></vitesse-toggle>
        </label>
        <!-- Only the off / no-ceiling states carry a caption. When a limit IS set, the figure lives
             in the input below — a "{{spent}} charged of {{limit}}" line and a "Default: 20× your
             plan…" note both just restated what the toggle and the input already show. -->
        ${!b.enabled
            ? html `<div class="billing-sub">${t('billing.budgetOffNote')}</div>`
            : limitUsd === null
                ? html `<div class="billing-sub">${t('billing.budgetNone')}</div>`
                : nothing}
        <!-- Said explicitly, because the obvious reading of a spending cap is the wrong one here. -->
        <p class="billing-note">${t('billing.budgetManualUnaffected')}</p>
        <div class="billing-field-row">
          <span class="billing-sub">${t('billing.budgetLabel')}</span>
          <vitesse-number-input
            class="billing-limit-input"
            size="sm"
            integer
            .min=${0}
            ?disabled=${!b.enabled}
            .value=${this.budgetDraft || (limitUsd === null ? '' : String(limitUsd))}
            @change=${(e) => (this.budgetDraft = e.detail.value)}
          ></vitesse-number-input>
          <button
            class="base-button base-button-sm base-button-blue"
            ?disabled=${!b.enabled}
            @click=${() => {
            const raw = this.budgetDraft.trim();
            // Empty means "no ceiling", deliberately not the same as zero, which means "never
            // charge automatically". Both are honoured as asked.
            emit({ limitUsd: raw === '' ? null : Number(raw) });
        }}
          >
            ${t('billing.budgetSave')}
          </button>
          ${b.overridden
            ? html `<button
                class="base-button base-button-sm"
                @click=${() => {
                this.budgetDraft = '';
                emit({ clear: true });
            }}
              >
                ${t('billing.budgetUsePlan')}
              </button>`
            : nothing}
        </div>
        ${this.budgetError ? html `<p class="billing-err">${this.budgetError}</p>` : nothing}
      </div>
    `;
    }
    submitAutoTopup(next) {
        this.autoTopupError = '';
        this.dispatchEvent(new CustomEvent('vitesse-billing-autotopup', { detail: next, bubbles: true, composed: true }));
    }
    /*
     * THE STORAGE PIE — the consolidated footprint split by what the customer recognises it AS
     * (conversations, knowledge, audits, AI config, files), the same conic-gradient chart the AI
     * usage page draws. The wedges sum to the total in the centre; a legend to the RIGHT names each
     * non-zero wedge with its size and share; hovering the pie shows the whole breakdown at once.
     */
    renderEntityPie(entities, total) {
        const slices = (entities ?? []).filter((s) => s.bytes > 0);
        if (!slices.length || total <= 0)
            return nothing;
        const pct = (n) => (n / total) * 100;
        let acc = 0;
        const stops = slices
            .map((s, i) => {
            const from = acc;
            acc += pct(s.bytes);
            return `${WEDGE_COLORS[i % WEDGE_COLORS.length]} ${from}% ${acc}%`;
        })
            .join(', ');
        const name = (k) => t(`billing.ent${k.charAt(0).toUpperCase()}${k.slice(1).replace(/_(.)/g, (_, c) => c.toUpperCase())}`);
        const tip = slices.map((s) => `${name(s.key)} · ${fmtBytes(s.bytes)} · ${pct(s.bytes).toFixed(0)}%`).join('\n');
        return html `
      <div class="billing-storage-viz">
        <div class="billing-donut" style=${`background: conic-gradient(${stops})`} role="img" title=${tip} aria-label=${tip}>
          <div class="billing-donut-center">
            <span class="billing-donut-big">${fmtBytes(total)}</span>
            <span class="billing-donut-sub">${t('billing.storageBreakdownTitle')}</span>
          </div>
        </div>
        <!-- Only the wedges that are actually in the pie, each with the colour it shows there. -->
        <ul class="billing-legend">
          ${slices.map((s, i) => html `<li>
              <span class="billing-legend-dot" style=${`background:${WEDGE_COLORS[i % WEDGE_COLORS.length]}`}></span>
              <span class="billing-legend-name">${name(s.key)}</span>
              <span class="billing-sub">${pct(s.bytes).toFixed(0)}%</span>
            </li>`)}
        </ul>
      </div>
    `;
    }
    renderStorage() {
        if (!this.usage)
            return this.error ? nothing : html `<p class="billing-sub">${t('billing.loading')}</p>`;
        const u = this.usage;
        const frac = u.fractionUsed;
        const over = frac !== null && frac >= 1;
        const near = frac !== null && frac >= WARN_AT && !over;
        const currentPeriod = u.periods?.[0] ?? { start: u.periodStart, end: u.periodEnd, gbMonths: u.gbMonths };
        return html `
      <div class="billing-card">
        <div class="billing-row billing-storage-row">
          <!-- LEFT: what this costs per month, in as much detail as the number has — dollars to the
               cent, or the precise sub-cent figure when storage is a fraction of one. The bytes are
               the sub-line; the money is the headline, because the money is what a bill shows. -->
          <div class="billing-storage-cost">
            <div class="billing-big">${t('billing.storagePerMo', { amount: fmtCostCents(u.overageGbMonths * u.overageCentsPerGbMonth) })}</div>
            <div class="billing-sub">${t('billing.storedLabel', { amount: fmtBytes(u.storedBytes) })}</div>
            ${over
            ? html `<p class="billing-note over">${u.enforcement === 'cap' ? t('billing.overCap') : t('billing.overMetered')}</p>`
            : near
                ? html `<p class="billing-note warn">${t('billing.nearLimit')}</p>`
                : nothing}
          </div>
          ${this.renderEntityPie(u.dataBytes?.byEntity, u.dataBytes?.total ?? u.storedBytes)}
        </div>
      </div>

      <div class="billing-card">
        <div class="billing-row">
          <h4 class="billing-h">${t('billing.usageHistory')}</h4>
          <span class="billing-sub">${t('billing.gbMonthsUnit')}</span>
        </div>
        <!-- GB-months, not gigabytes: storage is billed for bytes MULTIPLIED BY TIME. One line per
             period, the current one marked in progress; the table this replaced gave three columns
             to what one line per period says. -->
        <div class="billing-periods">
          ${(u.periods?.length ? u.periods : [currentPeriod]).map((p, i) => html `
              <div class="billing-period-row">
                <span>${fmtPeriod(p.start, p.end)}</span>
                <span class="billing-period-v">
                  ${p.gbMonths.toFixed(2)}
                  ${i === 0 ? html `<span class="base-field-hint">${t('billing.inProgress')}</span>` : nothing}
                </span>
              </div>
            `)}
        </div>
        ${u.overageGbMonths > 0
            ? html `<p class="billing-note over">${t('billing.overage', { n: u.overageGbMonths.toFixed(2), cost: fmtMoney(u.overageCents) })}</p>`
            : html `<p class="billing-note">${t('billing.withinPlan')}</p>`}
        <p class="billing-note">${t('billing.rate', { price: fmtMoney(u.overageCentsPerGbMonth) })}</p>
      </div>
    `;
    }
};
__decorate([
    property({ attribute: false })
], VitesseBillingPanel.prototype, "usage", void 0);
__decorate([
    property({ attribute: false })
], VitesseBillingPanel.prototype, "plan", void 0);
__decorate([
    property({ attribute: false })
], VitesseBillingPanel.prototype, "credits", void 0);
__decorate([
    property({ attribute: false })
], VitesseBillingPanel.prototype, "catalog", void 0);
__decorate([
    property({ type: Boolean })
], VitesseBillingPanel.prototype, "changingTier", void 0);
__decorate([
    property({ type: String })
], VitesseBillingPanel.prototype, "view", void 0);
__decorate([
    property({ type: Boolean })
], VitesseBillingPanel.prototype, "buying", void 0);
__decorate([
    property({ type: Boolean })
], VitesseBillingPanel.prototype, "checkoutOpen", void 0);
__decorate([
    property({ type: String })
], VitesseBillingPanel.prototype, "error", void 0);
__decorate([
    property({ type: String })
], VitesseBillingPanel.prototype, "autoTopupError", void 0);
__decorate([
    property({ type: String })
], VitesseBillingPanel.prototype, "budgetError", void 0);
__decorate([
    state()
], VitesseBillingPanel.prototype, "budgetDraft", void 0);
__decorate([
    property({ type: String })
], VitesseBillingPanel.prototype, "customUsd", void 0);
__decorate([
    property({ type: Boolean })
], VitesseBillingPanel.prototype, "addingCard", void 0);
__decorate([
    property({ type: String })
], VitesseBillingPanel.prototype, "autoThreshold", void 0);
__decorate([
    property({ type: String })
], VitesseBillingPanel.prototype, "autoAmount", void 0);
VitesseBillingPanel = __decorate([
    customElement('vitesse-billing-panel')
], VitesseBillingPanel);
export { VitesseBillingPanel };
/** Cents → display. Money is carried in cents precisely so no float ever rounds it; the division
 *  happens once, here, at the last possible moment. `null` = not sold. */
function fmtCents(cents) {
    // `$0` rather than the word "free": the tier's NAME is already Free, and a column reading
    // "Free / Free" says the same thing twice while hiding that this is a price.
    if (cents === null)
        return '$0';
    return `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
}
/**
 * A capability key as a human reads it: `telemetry.max_bandwidth_bytes` → `Telemetry max bandwidth
 * bytes`.
 *
 * NOT translated, and that is a deliberate limitation rather than an oversight. Capability keys are
 * DATA — they come from the catalog — so a lookup cannot be type-checked the way every other string
 * in this file is. A deterministic humanisation can never hide a row, which matters more: an
 * invisible row is a feature the customer pays for and cannot see.
 */
const ACRONYMS = new Set(['rbac', 'saml', 'sso', 'ai', 'api', 'plc', 'ot', 'it', 'id', 'gb', 'tb', 'mcp', 'iiot', '5g']);
function capLabel(key) {
    return key
        .replace(/[._]/g, ' ')
        .trim()
        .split(' ')
        .filter(Boolean)
        .map((w, i) => (ACRONYMS.has(w.toLowerCase()) ? w.toUpperCase() : i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
        .join(' ');
}
/**
 * One matrix cell, rendered.
 *
 * `null` and `0` are DIFFERENT answers: null on a numeric ceiling means unlimited, 0 means none.
 * Collapsing them is the mistake that tells a customer a plan includes something it forbids.
 *
 * Booleans render as a coloured mark: included is the accent blue, absent is the danger red — and
 * the marks differ in SHAPE too (check vs dash), so the row still reads without the colours.
 *
 * A key that names its unit gets it shown: `retention_hours: 720` used to render as a bare 720,
 * which a reader can take for days, dollars or a count. The unit comes from the KEY, so a renamed
 * label cannot strand the number.
 */
/** The three integration-shape caps, summed into one card feature. */
const INTEGRATION_MAXES = ['it_integrations.max_table', 'it_integrations.max_topic', 'it_integrations.max_endpoint'];
/** The synthetic key for that collapsed integration-connections feature row. */
const INTEGRATIONS_KEY = '__integrations';
/** A feature a tier does NOT carry — greyed with a dash rather than a check. Mirrors the absent
 *  spellings `cellValue` treats as "—": missing key, a false boolean, an empty list, `'none'`, or a
 *  zero retention window. */
function isFeatureOff(key, v) {
    if (v === undefined || v === false || v === 'none')
        return true;
    if (Array.isArray(v) && v.length === 0)
        return true;
    if (v === 0 && (key.endsWith('_hours') || key.endsWith('_days')))
        return true;
    return false;
}
function cellValue(key, v, tierId) {
    if (v === undefined)
        return html `<span class="cell-no">—</span>`;
    if (v === null)
        return t('billing.unlimited');
    if (typeof v === 'boolean')
        return v ? html `<span class="cell-yes">✓</span>` : html `<span class="cell-no">—</span>`;
    if (Array.isArray(v))
        return v.length ? String(v.length) : html `<span class="cell-no">—</span>`;
    if (typeof v === 'number') {
        if (v === 0 && (key.endsWith('_hours') || key.endsWith('_days')))
            return html `<span class="cell-no">—</span>`;
        if (key.endsWith('_hours'))
            return t('billing.hoursShort', { n: v });
        if (key.endsWith('_days'))
            return t('billing.daysShort', { n: v });
        // Trim a trailing .0 so a whole-number allowance reads "2 GB", not "2.0 GB" — the decimal is
        // there for "1.4 GB" and is noise on a round figure.
        return v >= 1_000_000 ? fmtBytes(v).replace(/\.0(?= )/, '') : String(v);
    }
    // The floor's telemetry is not device telemetry — it is only the AI-usage record, which is all a
    // no-plan org has. So "basic" reads as "AI usage" there, where a bare "Basic" implied more.
    if (key === 'telemetry' && v === 'basic' && tierId === 'free')
        return t('billing.freeTelemetry');
    if (v === 'none')
        return html `<span class="cell-no">—</span>`;
    // Enum levels read as words, capitalised: "Detection", "Isolation", "Paywall", "Basic".
    const w = String(v).replace(/_/g, ' ');
    return w.charAt(0).toUpperCase() + w.slice(1);
}
function fmtUsd(usd) {
    return `$${usd.toFixed(2)}`;
}
/** Cents as money. Whole cents here rather than sub-cent precision: this is an invoice line, and
 *  invoices do not have four decimal places. */
function fmtMoney(cents) {
    return `$${(cents / 100).toFixed(2)}`;
}
/**
 * A monthly cost in as much detail as the number carries.
 *
 * Storage is billed per GB-month at a fraction of a cent, and an org storing megabytes owes a
 * fraction of a cent a month — a figure that rounds to "$0.00" and vanishes. So above a cent it is
 * ordinary money (`$1.23`); below a cent but above zero it is shown to the microdollar (`$0.000300`)
 * so the customer sees the real, tiny number rather than a zero that is not quite true; and an
 * actual zero (a plan that includes it) is `$0.00`.
 */
function fmtCostCents(cents) {
    if (!(cents > 0))
        return '$0.00';
    if (cents >= 1)
        return `$${(cents / 100).toFixed(2)}`;
    return `$${(cents / 100).toFixed(6)}`;
}
function fmtPeriod(start, end) {
    // fmtDate, not toLocaleDateString: the app's chosen language, not the browser's.
    const f = (ms) => fmtDate(ms, { month: 'short', day: 'numeric' });
    // The end bound is exclusive, so the last day shown is the day before it — otherwise a month
    // reads as though it runs into the next one.
    return `${f(start)} – ${f(end - 1)}`;
}
//# sourceMappingURL=vitesse-billing-panel.js.map